import React from 'react'
import { createPortal } from 'react-dom'
import { useNavigate } from 'react-router-dom'
import { X, AlertTriangle, CreditCard, ArrowRight } from 'lucide-react'
import { componentClasses, cn } from '../utils/designSystem'

const CreditWarningModal = ({ 
  isOpen, 
  onClose, 
  actionType, 
  creditsRequired, 
  creditsRemaining, 
  packageName, 
  isFreeUser = false,
  message 
}) => {
  const navigate = useNavigate()

  const handleUpgrade = () => {
    onClose()
    navigate('/subscription')
  }

  const getActionDisplayName = (actionType) => {
    const actionNames = {
      chat: 'Chat',
      web_search: 'Web Search',
      academic_search: 'Academic Search',
      skripsi_agent: 'Skripsi Agent',
      artikel_agent: 'Artikel Agent',
      makalah_agent: 'Makalah Agent',
      gambar_agent: 'Gambar Agent',
      buku_agent: 'Buku Agent',
      berita_agent: 'Berita Agent',
      search_agent: 'Search Agent',
      trending_agent: 'Trending Agent',
      writing_agent: 'Writing Agent',
      ppt_agent: 'PPT Agent',
      chart_agent: 'Chart Agent'
    }
    return actionNames[actionType] || actionType
  }

  if (!isOpen) return null

  const modalContent = (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className={cn(
        componentClasses.card,
        "relative w-full max-w-md mx-auto bg-bg-primary border border-border-primary shadow-xl"
      )}>
        {/* Header */}
        <div className="flex items-center justify-between p-4 sm:p-6 border-b border-border-secondary">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 rounded-full bg-yellow-500/20 flex items-center justify-center">
              <AlertTriangle className="w-5 h-5 text-yellow-500" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-text-primary">
                Kredit Tidak Mencukupi
              </h3>
              <p className="text-sm text-text-secondary">
                {getActionDisplayName(actionType)}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-bg-secondary rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-text-tertiary" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 sm:p-6 space-y-4">
          {/* Credit Info */}
          <div className="bg-bg-secondary rounded-lg p-4 space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-sm text-text-secondary">Kredit Dibutuhkan:</span>
              <span className="text-sm font-medium text-text-primary">{creditsRequired}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-text-secondary">Kredit Tersisa:</span>
              <span className={cn(
                "text-sm font-medium",
                creditsRemaining === 0 ? 'text-red-400' : 'text-text-primary'
              )}>
                {creditsRemaining}
              </span>
            </div>
            {packageName && (
              <div className="flex justify-between items-center">
                <span className="text-sm text-text-secondary">Paket Saat Ini:</span>
                <span className={cn(
                  "text-sm font-medium px-2 py-1 rounded",
                  packageName === 'FREE' 
                    ? 'bg-gray-500/20 text-gray-400'
                    : 'bg-brand-primary/20 text-brand-primary'
                )}>
                  {packageName}
                </span>
              </div>
            )}
          </div>

          {/* Message */}
          <div className="text-sm text-text-secondary leading-relaxed">
            {message || (
              isFreeUser 
                ? `Anda menggunakan paket FREE dengan kredit terbatas. Untuk menggunakan ${getActionDisplayName(actionType)}, Anda memerlukan ${creditsRequired} kredit tetapi hanya memiliki ${creditsRemaining} kredit.`
                : `Kredit Anda tidak mencukupi untuk menggunakan ${getActionDisplayName(actionType)}. Dibutuhkan ${creditsRequired} kredit, tetapi Anda hanya memiliki ${creditsRemaining} kredit.`
            )}
          </div>

          {isFreeUser && (
            <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-3">
              <p className="text-sm text-blue-400">
                💡 <strong>Tips:</strong> Dengan paket berbayar, Anda akan mendapatkan lebih banyak kredit dan akses ke semua fitur premium.
              </p>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex flex-col sm:flex-row gap-3 p-4 sm:p-6 border-t border-border-secondary">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2 text-sm font-medium text-text-secondary bg-bg-secondary hover:bg-bg-tertiary rounded-lg transition-colors"
          >
            Tutup
          </button>
          <button
            onClick={() => {
              // Trigger a refresh of subscription data
              window.dispatchEvent(new CustomEvent('refreshSubscription'))
              onClose()
            }}
            className="px-4 py-2 text-sm font-medium text-blue-400 bg-blue-500/10 hover:bg-blue-500/20 rounded-lg transition-colors flex items-center justify-center space-x-2"
          >
            <span>Refresh</span>
          </button>
          <button
            onClick={handleUpgrade}
            className="flex-1 px-4 py-2 text-sm font-medium text-text-primary bg-brand-primary hover:bg-brand-secondary rounded-lg transition-colors flex items-center justify-center space-x-2"
          >
            <CreditCard className="w-4 h-4" />
            <span>Upgrade Paket</span>
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  )

  return createPortal(modalContent, document.body)
}

export default CreditWarningModal